/**
 * A tool call's arguments against the tool's JSON schema (CACHE step 2,
 * 2026-10-19). On TOOL_DELIVERY fixed a loaded browser tool is called through
 * use_tool, which the provider checks only as `{name, args}`; this checks the
 * args against the named tool's own schema before the tool runs, and says what
 * is wrong by field so the model can fix its call.
 *
 * The browser tools' schemas use a small part of JSON Schema: type, properties,
 * required, enum, items, additionalProperties, the numeric bounds, the length
 * bounds and maxItems/minItems. Anything else in a schema is not checked here;
 * the tool's executor still checks what it needs.
 */

type Schema = Record<string, unknown>

const isObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v)

/** The JSON type of a value, as a schema names it. */
function jsonType(v: unknown): string {
  if (v === null) return 'null'
  if (Array.isArray(v)) return 'array'
  if (typeof v === 'number') return Number.isInteger(v) ? 'integer' : 'number'
  return typeof v
}

function matchesType(type: unknown, v: unknown): boolean {
  const actual = jsonType(v)
  if (type === 'number') return actual === 'number' || actual === 'integer'
  return type === actual
}

const quoted = (v: unknown) => (typeof v === 'string' ? `'${v}'` : JSON.stringify(v))

function check(schema: unknown, value: unknown, path: string, out: string[]): void {
  if (!isObject(schema)) return
  const s = schema as Schema
  if (s.type !== undefined) {
    const types = Array.isArray(s.type) ? s.type : [s.type]
    if (!types.some((t) => matchesType(t, value))) {
      out.push(`${path}: expected ${types.join(' or ')}, got ${jsonType(value)}`)
      return
    }
  }
  if (Array.isArray(s.enum) && !s.enum.some((e) => JSON.stringify(e) === JSON.stringify(value)))
    out.push(`${path}: must be one of ${s.enum.map(quoted).join(', ')}`)
  if (typeof value === 'string') {
    if (typeof s.minLength === 'number' && value.length < s.minLength)
      out.push(`${path}: at least ${s.minLength} character${s.minLength === 1 ? '' : 's'}`)
    if (typeof s.maxLength === 'number' && value.length > s.maxLength)
      out.push(`${path}: at most ${s.maxLength} characters`)
  }
  if (typeof value === 'number') {
    if (typeof s.minimum === 'number' && value < s.minimum)
      out.push(`${path}: must be at least ${s.minimum}`)
    if (typeof s.maximum === 'number' && value > s.maximum)
      out.push(`${path}: must be at most ${s.maximum}`)
    if (typeof s.exclusiveMinimum === 'number' && value <= s.exclusiveMinimum)
      out.push(`${path}: must be more than ${s.exclusiveMinimum}`)
    if (typeof s.exclusiveMaximum === 'number' && value >= s.exclusiveMaximum)
      out.push(`${path}: must be less than ${s.exclusiveMaximum}`)
  }
  if (Array.isArray(value)) {
    if (typeof s.minItems === 'number' && value.length < s.minItems)
      out.push(`${path}: at least ${s.minItems} items`)
    if (typeof s.maxItems === 'number' && value.length > s.maxItems)
      out.push(`${path}: at most ${s.maxItems} items`)
    if (isObject(s.items)) value.forEach((item, i) => check(s.items, item, `${path}[${i}]`, out))
  }
  if (isObject(value)) {
    const properties = isObject(s.properties) ? s.properties : {}
    const required = Array.isArray(s.required) ? (s.required as string[]) : []
    for (const field of required)
      if (!(field in value)) out.push(`${path}: missing required field '${field}'`)
    for (const [field, v] of Object.entries(value)) {
      // A model sends null for a field it means to leave out: read it so.
      if (v === null && !required.includes(field)) continue
      if (field in properties) check(properties[field], v, `${path}.${field}`, out)
      else if (s.additionalProperties === false) out.push(`${path}: unknown field '${field}'`)
      else if (isObject(s.additionalProperties))
        check(s.additionalProperties, v, `${path}.${field}`, out)
    }
  }
}

/**
 * What is wrong with `args` for this schema, one line per problem with the
 * field it is about (`args.filters[0].op: must be one of ...`); empty when the
 * arguments match.
 */
export function validateArgs(schema: unknown, args: unknown): string[] {
  const out: string[] = []
  check(schema, args, 'args', out)
  return out
}
